import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { formatCurrency } from 'utils/CommonFunction';

// One "label ......... amount" line of a revenue breakdown. Shared by the
// Country-wise Net Revenue cards and the Revenue Overview "Selected Period"
// panel, which used to carry their own copies of this markup.
//
// Variants exist only to preserve each panel's existing typography, including
// the minus-sign spacing, so extracting this changed no pixels:
//   muted  - Country-wise Net Revenue cards
//   row    - a normal Selected Period line (Gross Revenue, Refund, VAT, costs)
//   total  - a Selected Period subtotal (Net Revenue, Commissionable revenue)
const VARIANTS = {
  muted: {
    rowProps: {},
    labelProps: { fontSize: 'sm', color: 'gray.500' },
    valueProps: { fontSize: 'sm', fontWeight: '500', color: 'gray.700' },
    negativeSign: '-',
  },
  row: {
    rowProps: { py: '1' },
    labelProps: { fontWeight: 'medium', color: '#292D32' },
    valueProps: { fontSize: 'sm' },
    negativeSign: '- ',
  },
  total: {
    rowProps: { py: '1' },
    labelProps: { fontSize: 'md', fontWeight: 'bold', color: '#292D32' },
    valueProps: { fontSize: 'md', fontWeight: 'bold', color: '#292D32' },
    negativeSign: '- ',
  },
};

const BreakdownRow = ({
  label,
  value,
  currency,
  negative = false,
  variant = 'row',
  badge = null,
}) => {
  const styles = VARIANTS[variant] || VARIANTS.row;

  return (
    <Flex justify="space-between" align="center" {...styles.rowProps}>
      <Flex align="center" gap="2">
        <Text {...styles.labelProps}>{label}</Text>
        {badge}
      </Flex>
      <Text
        {...styles.valueProps}
        color={negative ? 'red.500' : styles.valueProps.color}
      >
        {negative ? styles.negativeSign : ''}
        {formatCurrency(value, currency)}
      </Text>
    </Flex>
  );
};

BreakdownRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  currency: PropTypes.string,
  // Deductions: shows a leading minus and, where the panel asks for it, red text.
  negative: PropTypes.bool,
  variant: PropTypes.oneOf(['muted', 'row', 'total']),
  badge: PropTypes.node,
};

export default BreakdownRow;
